import { isBoolean, isEmpty, isEqual, toString } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { AuthDataProvider } from 'src/components/authProvider';
import { MAX_ASSEMBLY_ROTATION_DURATION_SECONDS } from 'src/constants';
import {
  AssemblyJourneys,
  CodJourney,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import {
  useAssemblyTransaction,
  useCreateAssemblyVariation,
  useGetAssembly,
  useGetComponentTypes,
  useGetLayoutScreens,
  useUpdateAssembly,
  useUpdateCreateDeleteAssemblyComponents,
  useViewAssemblyComponents,
} from 'src/hooks';
import { useGetBulkReplaceRequestNamesByAssemblyIds } from 'src/hooks/api/domainSpecific/bulkReplace/useGetBulkReplaceRequestNamesByAssemblyIds';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import { useCodEvents } from 'src/hooks/useCodEvents';
import { useDaypartGlobalComponent } from 'src/hooks/useDaypartGlobalComponent';
import {
  IAssembly,
  IAssemblyComponent,
  IComponent,
  ISectionConfig,
  IStatus,
} from 'src/interfaces';
import { GcfMockData } from 'src/utils/gcf-helper';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { checkNumOfComponents } from '../EditAssemblyPage.helper';

const normalizeComponents = (items: IAssemblyComponent[]) =>
  (items ?? [])
    .map((item) => ({
      component_id: item.component_id,
      layout_screen_id: item.layout_screen_id,
      screen_section_id: item.screen_section_id,
      rotation_order: item.rotation_order,
      backup_order: item.backup_order,
      config: item.config,
      start_date: item.start_date,
      end_date: item.end_date,
      allow_overrides: item.allow_overrides,
    }))
    .sort((a, b) => {
      if (a.layout_screen_id !== b.layout_screen_id)
        return (a.layout_screen_id ?? 0) - (b.layout_screen_id ?? 0);
      if (a.screen_section_id !== b.screen_section_id)
        return (a.screen_section_id ?? 0) - (b.screen_section_id ?? 0);
      if (a.rotation_order !== b.rotation_order)
        return a.rotation_order - b.rotation_order;

      return (a.backup_order ?? 0) - (b.backup_order ?? 0);
    });

export const useEditAssemblyPage = (assemblyId?: string) => {
  const updateAssembly = useUpdateAssembly();
  const createAssemblyVariation = useCreateAssemblyVariation();
  const updateCreateDeleteAssemblyComponents =
    useUpdateCreateDeleteAssemblyComponents();
  const assemblyTransaction = useAssemblyTransaction();

  const [replaceAssemblyComponentId, setReplaceAssemblyComponentId] =
    useState<string>();
  const [openSaveComponentModal, setOpenSaveComponentModal] = useState(false);
  const [openComponentListModal, setOpenComponentListModal] = useState(false);
  const [openSectionDrawer, setOpenSectionDrawer] = useState(false);
  const [openCreateVariationModal, setOpenCreateVariationModal] =
    useState(false);
  const [activeAssemblyJourney, setActiveAssemblyJourney] = useState<
    AssemblyJourneys | undefined
  >();
  const [selectedSection, setSelectedSection] = useState<
    | {
        layoutScreenId: number;
        screenSectionId: number;
        sectionId: number;
        width: number;
        height: number;
        config?: ISectionConfig;
      }
    | undefined
  >();

  const { userRole } = useContext(AuthDataProvider);
  const { data: componentTypes = [] } = useGetComponentTypes();

  const assemblyIdNumber = Number(assemblyId);

  const { data: stagedAssemblies } = useGetBulkReplaceRequestNamesByAssemblyIds(
    {
      assembly_ids: [assemblyIdNumber],
      statuses: ['staged'],
    },
  );

  const stagedAssemblyIds = new Set(
    stagedAssemblies?.map(
      (item: { assembly_id: number }) => item.assembly_id,
    ) ?? [],
  );

  const isAssemblyIdMatched = stagedAssemblyIds.has(assemblyIdNumber);

  const {
    data: assemblyDetails,
    isLoading: isAssemblyDetailsLoading,
    error,
  } = useGetAssembly(toString(assemblyId));

  useApiErrorNavigator(error);

  const { data: layouts = [], isLoading: isLayoutScreensLoading } =
    useGetLayoutScreens(
      assemblyDetails?.layout_id,
      Boolean(assemblyDetails?.layout_id),
    );

  const {
    data: [assemblyComponentsDetails] = [],
    isLoading: isAssemblyComponentsLoading,
  } = useViewAssemblyComponents(
    [
      `id=${assemblyId}`,
      `omitCovered=${!Boolean(assemblyDetails?.parentAssembly)}`,
    ],
    Boolean(assemblyId),
  );

  const { globalComponentsData, onChangeDaypart, selectedDaypartId } =
    useDaypartGlobalComponent(layouts?.[0]?.layout_type_id);

  const mergeComponentsWithData = (
    data: IAssemblyComponent[],
    components: IComponent[],
  ): (IAssemblyComponent & { component?: IComponent })[] => {
    return data?.map((assembly) => {
      const existingComponent = components?.find(
        (component) => component.id === assembly?.component_id,
      );

      return {
        ...assembly,
        ...(existingComponent && { component: existingComponent }),
      };
    });
  };

  const {
    components = [],
    data = [],
    backupAssemblyComponents = [],
  } = assemblyComponentsDetails || {};

  const assemblyComponents = mergeComponentsWithData(data, components);
  const backupComponents = mergeComponentsWithData(
    backupAssemblyComponents,
    components,
  );

  const formMethods = useForm<
    Partial<IAssembly> & {
      assemblyComponents: IAssemblyComponent[];
      backupAssemblyComponents: IAssemblyComponent[];
      components: IComponent[];
    }
  >({
    defaultValues: {
      assemblyComponents: [],
      backupAssemblyComponents: [],
      components: [],
    },
    mode: 'onChange',
  });

  const cleanSnapshot = useRef<{
    assemblyComponents: ReturnType<typeof normalizeComponents>;
    backupAssemblyComponents: ReturnType<typeof normalizeComponents>;
  } | null>(null);

  const isDataReady =
    !isAssemblyDetailsLoading &&
    !isAssemblyComponentsLoading &&
    !isLayoutScreensLoading &&
    !!assemblyDetails &&
    !!assemblyComponentsDetails;

  useEffect(() => {
    if (!isDataReady) return;

    const snapshot = {
      ...assemblyDetails,
      assemblyComponents: assemblyComponents ?? [],
      backupAssemblyComponents: backupComponents ?? [],
      components: components ?? [],
    };

    cleanSnapshot.current = {
      assemblyComponents: normalizeComponents(assemblyComponents ?? []),
      backupAssemblyComponents: normalizeComponents(backupComponents ?? []),
    };

    formMethods.reset(snapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataReady, assemblyDetails, assemblyComponentsDetails]);

  // Subscribe to live component values so isActuallyDirty reacts on every change.
  const watchedAssemblyComponents = useWatch({
    control: formMethods.control,
    name: 'assemblyComponents',
  });
  const watchedBackupComponents = useWatch({
    control: formMethods.control,
    name: 'backupAssemblyComponents',
  });

  const { dirtyFields } = formMethods.formState;

  const isActuallyDirty =
    !!dirtyFields.name ||
    !!dirtyFields.assemblyTags ||
    (cleanSnapshot.current !== null &&
      !isEqual(
        normalizeComponents(watchedAssemblyComponents ?? []),
        cleanSnapshot.current.assemblyComponents,
      )) ||
    (cleanSnapshot.current !== null &&
      !isEqual(
        normalizeComponents(watchedBackupComponents ?? []),
        cleanSnapshot.current.backupAssemblyComponents,
      ));

  const formFieldArray = useFieldArray({
    control: formMethods.control,
    name: 'assemblyComponents',
    keyName: 'customID',
  });

  const backupFieldArray = useFieldArray({
    control: formMethods.control,
    name: 'backupAssemblyComponents',
    keyName: 'backupFieldArray',
  });

  const [customerState, setCustomerState] = useState<Partial<GcfMockData>>(
    GcfMockData.COD_CUSTOMER_UNKNOWN,
  );

  useCodEvents(
    customerState,
    activeAssemblyJourney as CodJourney,
    selectedDaypartId,
  );

  const onChangeRemoveComponentId = (componentId: number) => {
    // to be implemented
    return componentId;
  };

  const configDetails = formMethods.getValues('config');

  const isValidDuration = (item?: any) =>
    typeof item.duration === 'number' &&
    item.duration > 0 &&
    item.duration <= MAX_ASSEMBLY_ROTATION_DURATION_SECONDS;

  const isSectionRotationValid = configDetails
    ? Object.values(configDetails).every((obj) =>
        Object.entries(obj).every(([key, val]) => key && isValidDuration(val)),
      )
    : true;

  const isChildAssembly = Boolean(formMethods.getValues().variation_parent_id);
  const hasAllComponents =
    isChildAssembly ||
    checkNumOfComponents(layouts, formMethods.getValues('assemblyComponents'));

  const shouldBlock =
    isActuallyDirty && formMethods.getValues('status') === IStatus.DRAFT;

  const canSaveAsDraft =
    isEmpty(formMethods.formState.errors) &&
    formMethods.getValues('status') !== IStatus.READY &&
    !updateAssembly.isLoading &&
    !updateCreateDeleteAssemblyComponents.isLoading &&
    !isAssemblyDetailsLoading &&
    !isLayoutScreensLoading &&
    !isAssemblyComponentsLoading &&
    !isAssemblyIdMatched &&
    userRole !== AuthClassification.OWNER_OPERATOR;
  const canSaveAsReady =
    hasAllComponents &&
    isEmpty(formMethods.formState.errors) &&
    formMethods.getValues('status') !== IStatus.READY &&
    !updateAssembly.isLoading &&
    !updateCreateDeleteAssemblyComponents.isLoading &&
    !isAssemblyDetailsLoading &&
    !isLayoutScreensLoading &&
    !isAssemblyComponentsLoading &&
    !isAssemblyIdMatched &&
    userRole !== AuthClassification.OWNER_OPERATOR &&
    isSectionRotationValid;
  const disableRemoveVariation =
    !isAssemblyDetailsLoading && userRole !== AuthClassification.OWNER_OPERATOR;
  const canEdit =
    formMethods.getValues('status') !== IStatus.READY &&
    userRole !== AuthClassification.OWNER_OPERATOR;
  const parentAssembly = isChildAssembly
    ? formMethods.getValues().parentAssembly
    : formMethods.getValues();
  const isParentAssemblyReady = parentAssembly?.status === IStatus.READY;

  useEffect(() => {
    if (!canEdit) {
      setOpenComponentListModal(false);
      setOpenSaveComponentModal(false);
      setOpenSectionDrawer(false);
      setOpenCreateVariationModal(false);
    }
  }, [canEdit]);

  useEffect(() => {
    const sectionTypes = layouts?.[0]?.config?.sectionTypes;
    const { hasJourney } = checkSectionTypes(sectionTypes);
    setActiveAssemblyJourney(
      hasJourney
        ? CodJourney.CUSTOMER_ENTERS_DRIVE_THRU
        : SectionJourney.DEFAULT,
    );
  }, [assemblyDetails?.config, layouts]);

  return {
    replaceAssemblyComponentId,
    openSaveComponentModal,
    openComponentListModal,
    openSectionDrawer,
    openCreateVariationModal,
    updateAssembly,
    updateCreateDeleteAssemblyComponents,
    createAssemblyVariation,
    formMethods,
    formFieldArray,
    backupFieldArray,
    layouts,
    selectedSection,
    isAssemblyDetailsLoading,
    canSaveAsDraft,
    canSaveAsReady,
    disableRemoveVariation,
    canEdit,
    parentAssembly,
    parentAssemblyComponents:
      assemblyComponentsDetails?.parentAssemblyComponents ?? [],
    isParentAssemblyReady,
    componentTypes,
    onToggleSaveComponentModal: (active?: boolean) =>
      setOpenSaveComponentModal((state) =>
        isBoolean(active) ? active : !state,
      ),
    onChangeSelectedSection: setSelectedSection,
    onReplacedAssemblyComponent: setReplaceAssemblyComponentId,
    onToggleComponentListModal: (active?: boolean) =>
      setOpenComponentListModal((state) =>
        isBoolean(active) ? active : !state,
      ),
    onToggleSectionDrawer: (active?: boolean) =>
      setOpenSectionDrawer((state) => {
        return isBoolean(active) ? active : !state;
      }),
    onToggleCreateVariationModal: (active?: boolean) =>
      setOpenCreateVariationModal((state) => {
        return isBoolean(active) ? active : !state;
      }),
    onChangeRemoveComponentId,
    parentBackupAssemblyComponents:
      assemblyComponentsDetails?.parentBackupAssemblyComponents ?? [],
    activeAssemblyJourney,
    setActiveAssemblyJourney,
    customerState,
    setCustomerState,
    shouldBlock,
    assemblyComponentsDetails,
    assemblyTransaction,
    globalComponentsData,
    onChangeDaypart,
    selectedDaypartId,
  };
};
